import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/ui/sonner";
import {
  BLOCK_TYPE_ORDER,
  createBlueprint,
  deleteBlueprint,
  duplicateBlueprint,
  ensureSeedBlueprints,
  listBlocks,
  listBlueprints,
  updateBlueprint,
  type ComposerBlock,
  type ComposerBlockType,
  type ComposerBlueprint,
  type ComposerBlueprintBlockRef,
} from "@/lib/composerBlocks";

export type EditorState = {
  open: boolean;
  editingId: string | null;
  title: string;
  description: string;
  contentTypeHint: string;
  blockRefs: ComposerBlueprintBlockRef[];
};

export const emptyEditor: EditorState = {
  open: false,
  editingId: null,
  title: "",
  description: "",
  contentTypeHint: "",
  blockRefs: [],
};

export const useBlueprintsPanel = (workspaceId: string | undefined) => {
  const qc = useQueryClient();
  const [editor, setEditor] = useState<EditorState>(emptyEditor);

  // Lazy-seed once per workspace open.
  useEffect(() => {
    if (!workspaceId) return;
    ensureSeedBlueprints(workspaceId)
      .then(() => qc.invalidateQueries({ queryKey: ["composer-blueprints", workspaceId] }))
      .catch((e: Error) => console.error("ensureSeedBlueprints", e));
  }, [workspaceId, qc]);

  const { data: blocks = [] } = useQuery({
    queryKey: ["composer-blocks", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => (workspaceId ? listBlocks(workspaceId) : Promise.resolve([])),
  });

  const { data: blueprints = [] } = useQuery({
    queryKey: ["composer-blueprints", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => (workspaceId ? listBlueprints(workspaceId) : Promise.resolve([])),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["composer-blueprints", workspaceId] });
    qc.invalidateQueries({ queryKey: ["composer-block-usage", workspaceId] });
  };

  const createMut = useMutation({
    mutationFn: async (values: Omit<EditorState, "open" | "editingId">) => {
      if (!workspaceId) throw new Error("No workspace");
      return createBlueprint(workspaceId, {
        title: values.title.trim(),
        description: values.description.trim(),
        contentTypeHint: values.contentTypeHint.trim() || undefined,
        blockRefs: values.blockRefs,
      });
    },
    onSuccess: () => {
      invalidate();
      toast.success("Blueprint created");
      setEditor(emptyEditor);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMut = useMutation({
    mutationFn: async (values: EditorState) => {
      if (!values.editingId) throw new Error("No blueprint id");
      await updateBlueprint(values.editingId, {
        title: values.title.trim(),
        description: values.description.trim(),
        contentTypeHint: values.contentTypeHint.trim() || null,
        blockRefs: values.blockRefs,
      });
    },
    onSuccess: () => {
      invalidate();
      toast.success("Blueprint updated");
      setEditor(emptyEditor);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteBlueprint(id),
    onSuccess: () => {
      invalidate();
      toast.success("Blueprint deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const duplicateMut = useMutation({
    mutationFn: (bp: ComposerBlueprint) => {
      if (!workspaceId) throw new Error("No workspace");
      return duplicateBlueprint(workspaceId, bp);
    },
    onSuccess: () => {
      invalidate();
      toast.success("Blueprint duplicated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const openCreate = () =>
    setEditor({ ...emptyEditor, open: true, blockRefs: [{ type: "global" }] });

  const openEdit = (bp: ComposerBlueprint) =>
    setEditor({
      open: true,
      editingId: bp.id,
      title: bp.title,
      description: bp.description,
      contentTypeHint: bp.contentTypeHint ?? "",
      blockRefs: bp.blockRefs.map((r) => ({ ...r })),
    });

  const updateRef = (idx: number, patch: Partial<ComposerBlueprintBlockRef>) =>
    setEditor((s) => {
      const next = s.blockRefs.slice();
      next[idx] = { ...next[idx], ...patch };
      return { ...s, blockRefs: next };
    });

  const removeRef = (idx: number) =>
    setEditor((s) => ({ ...s, blockRefs: s.blockRefs.filter((_, i) => i !== idx) }));

  const moveRef = (idx: number, dir: -1 | 1) =>
    setEditor((s) => {
      const j = idx + dir;
      if (j < 0 || j >= s.blockRefs.length) return s;
      const next = s.blockRefs.slice();
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...s, blockRefs: next };
    });

  const addRef = () =>
    setEditor((s) => ({ ...s, blockRefs: [...s.blockRefs, { type: "global" }] }));

  const blocksByType = useMemo(() => {
    const m = new Map<ComposerBlockType, ComposerBlock[]>();
    for (const t of BLOCK_TYPE_ORDER) m.set(t, []);
    for (const b of blocks) m.get(b.type)?.push(b);
    return m;
  }, [blocks]);

  return {
    editor,
    setEditor,
    blocks,
    blueprints,
    blocksByType,
    createMut,
    updateMut,
    deleteMut,
    duplicateMut,
    openCreate,
    openEdit,
    updateRef,
    removeRef,
    moveRef,
    addRef,
  };
};
